/**
 * idb/useIndexedDBStore.js
 * ---------------------------------------------------------------------------
 * A React hook that binds a given `storeName` once and returns a STABLE object
 * of promise-returning IndexedDB methods (memoized on `storeName`), so it is safe
 * in dependency arrays and won't re-render consumers on every parent render.
 *
 * Backed by the main-thread `idbClient` (formerly a Web Worker). The returned
 * shape is unchanged:
 *   { getAll, add, update, get, put, insertIfAbsent, clearMessages, cleanOrphans }
 */

import { useMemo } from 'react';
import { idbClient } from './idb-client';

/**
 * @param {string} storeName - object store to bind (e.g. 'messages')
 * @returns {object} store-bound, promise-returning IndexedDB methods
 */
export const useIndexedDBStore = (storeName) => useMemo(() => ({
  // Generic CRUD, pre-bound to this store so callers pass only data.
  getAll: () => idbClient.getAll(storeName),
  add: (record) => idbClient.add(storeName, record),
  update: (record) => idbClient.update(storeName, record),
  get: (key) => idbClient.get(storeName, key),
  put: (record) => idbClient.put(storeName, record),

  // Cursor-driven message operations (store is implied by the op itself).
  insertIfAbsent: (record, matchField) => idbClient.insertIfAbsent(storeName, record, matchField),
  clearMessages: (opts) => idbClient.clearMessages(opts),
  cleanOrphans: (opts) => idbClient.cleanOrphans(opts),
}), [storeName]);
